import { ComponentEntity, stringifyEntityRef } from "@backstage/catalog-model";
import { DiscoveryApi, discoveryApiRef, FetchApi, fetchApiRef, alertApiRef, useApi, useRouteRef } from "@backstage/core-plugin-api";
import { scaffolderPlugin } from "@backstage/plugin-scaffolder";
import { scaffolderApiRef } from "@backstage/plugin-scaffolder-react";
import { MessagingResource } from "@internal/open-catalog";
import { Accordion, Button, createStyles, List, ListItem, ListItemIcon, ListItemText, makeStyles, Theme, Typography, withStyles } from "@material-ui/core";
import MuiAccordionDetails from '@material-ui/core/AccordionDetails';
import MuiAccordionSummary from '@material-ui/core/AccordionSummary';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import EditIcon from '@material-ui/icons/Edit';
import PublishIcon from '@material-ui/icons/Publish';
import DoneSharpIcon from '@material-ui/icons/DoneSharp';
import { GitIcon } from "../../../../components/icons";

import { DraftStatus, DraftViewBySystem } from "@internal/backstage-plugin-amqp-topology-manager-common";
import { default as React, useCallback, useEffect, useState } from 'react';
import { useNavigate } from "react-router";
import SimpleConfirmDialog from "../../../../components/dialog";
import { SpringBootIcon } from "../../../../components/icons";
import { TopologyEditDialog } from "./components/topology";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      gap: theme.spacing(1),
      alignItems: 'center'
    }
  })
);

const ServiceDraftItem = (props: {
  drafts: DraftViewBySystem[],
  service: ComponentEntity,
  entity: ComponentEntity,
  onServiceSelectionChanged: () => void,
  onDraftDeleted: () => void,
  onRequestDraftDelete: (callDelete: () => Promise<any>) => void
}) => {

  const { drafts, service, entity, onServiceSelectionChanged, onRequestDraftDelete, onDraftDeleted } = props;
  const { title, name, description } = service.metadata;

  const serviceRef = stringifyEntityRef(service);
  const classes = useStyles();

  const [draftView, setDraftView] = useState<DraftViewBySystem | undefined>(undefined);

  useEffect(() => {
    setDraftView(drafts?.find(draft => draft.component_entity_ref === serviceRef));
  }, [drafts, serviceRef]);

  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const scaffolderApi = useApi(scaffolderApiRef);
  const alertApi = useApi(alertApiRef);
  const navigate = useNavigate();
  const taskRoute = useRouteRef(scaffolderPlugin.routes.ongoingTask);

  const handlePR = async () => {
    if (draftView?.pull_request_url) {
      const url = new URL(draftView.pull_request_url);
      if (url.protocol === 'https:') {
        window.open(url.toString(), '_blank', 'noopener,noreferrer');
      }
      return;
    }

    try {
      const { taskId } = await scaffolderApi.scaffold({
        templateRef: 'topology-management-template',
        values: {
          componentEntityRef: serviceRef,
          systemEntityRef: stringifyEntityRef(entity),
        },
      });

      navigate(taskRoute({ taskId }));
    } catch (e) {
      alertApi.post({ message: `Erro ao iniciar o PR: ${e instanceof Error ? e.message : e}`, severity: 'error' });
    }
  };

  const handleDiscard = () =>
    discoveryApi.getBaseUrl('amqp-topology-manager')
      .then(base => {
        const url = new URL(`${base}/drafts`);
        url.searchParams.append('componentEntityRef', serviceRef);
        
        return fetchApi.fetch(url, { method: 'DELETE' })
      }).then(() => onDraftDeleted())

  const onDiscardChanges = async () => {
    if (!draftView) return;
    if (draftView.status === DraftStatus.SUBMITTED) {
      await handleDiscard();
      await triggerAmqpCatalogRefresh(discoveryApi, fetchApi);
      return;
    }
    onRequestDraftDelete(handleDiscard);
  }

  const [prButtonTitle, setPrButtonTitle] = useState('Pull request');
  const [changeTopologyButtonTitle, setChangeTopologyButtonTitle] = useState('Change topology');
  const [discardChangesButtonTitle, setDiscardChangesButtonTitle] = useState('Discard changes');

  useEffect(() => {
    if (draftView?.status === DraftStatus.SUBMITTED) {
      setPrButtonTitle('View pull request');
      setChangeTopologyButtonTitle('Cannot change topology until last draft is applied');
      setDiscardChangesButtonTitle('Finish the draft and request amqp topology refresh');
      return;
    }
    setPrButtonTitle('Publish draft');
    setChangeTopologyButtonTitle('Change topology');
    setDiscardChangesButtonTitle('Discard changes');
  }, [draftView]);

  return (
    <ListItem className={classes.root}>
      <ListItemIcon><SpringBootIcon /></ListItemIcon>
      <ListItemText secondary={description}>
        <Typography variant='body2'>
          {title ? title : name}
        </Typography>
      </ListItemText>
      <ListItemIcon>
        <Button
          children={draftView?.status === DraftStatus.SUBMITTED ? <GitIcon /> : <PublishIcon color={draftView ? "secondary" : "disabled"} />}
          disabled={!draftView}
          title={prButtonTitle}
          onClick={handlePR}
        />
      </ListItemIcon>
      <ListItemIcon>
        <Button children={<EditIcon />} disabled={draftView?.status === DraftStatus.SUBMITTED} title={changeTopologyButtonTitle} onClick={onServiceSelectionChanged} />
      </ListItemIcon>
      <ListItemIcon>
        <Button children={draftView?.status === DraftStatus.SUBMITTED ? <DoneSharpIcon /> : <DeleteForeverIcon />} disabled={!draftView} title={discardChangesButtonTitle} onClick={onDiscardChanges} />
      </ListItemIcon>
    </ListItem>
  );
}

const AccordionSummary = withStyles({
  root: {
    borderBottom: '1px solid rgba(0, 0, 0, .125)',
    marginBottom: -1,
    minHeight: 56,
    fontWeight: 600,
    '&$expanded': {
      minHeight: 56,
    },
  },
  content: {
    '&$expanded': {
      margin: '12px 0',
    },
  },
  expanded: {},
})(MuiAccordionSummary);

const AccordionDetails = withStyles((theme) => ({
  root: {
    padding: theme.spacing(1),
    '& > ul': {
      width: '100%'
    },
    '& li:hover': {
      backgroundColor: '#f2f2f2'
    }
  },
}))(MuiAccordionDetails);

export const SystemMessagingTopologyEditPage = (props: {
  services: ComponentEntity[], entity: ComponentEntity, reloadResources: () => Promise<MessagingResource[]>
}) => {

  const { services, entity, reloadResources } = props;

  const [openEditDialog, setOpenEditDialog] = React.useState(false);
  const [service, setService] = useState<ComponentEntity>();

  const [drafts, setDrafts] = useState<DraftViewBySystem[] | undefined>(undefined);

  const [deleteDraftConfirmOpen, setDeleteDraftConfirmOpen] = useState(false);
  const [callDraftDelete, setCallDraftDelete] = useState(() => () => Promise.resolve());

  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const fetchDraftsForSystem = useCallback(() => {
    const systemRef = stringifyEntityRef(entity);

    return discoveryApi.getBaseUrl('amqp-topology-manager')
      .then(base => {
        const url = new URL(`${base}/drafts/system`);
        url.searchParams.append('systemEntityRef', systemRef);
        return fetchApi.fetch(url);
      })
      .then(response => response.ok ? response.json() : undefined)
      .then(setDrafts);
  }, [discoveryApi, fetchApi, entity]);

  useEffect(() => {
    if (openEditDialog || deleteDraftConfirmOpen) return;
    fetchDraftsForSystem();
  }, [fetchDraftsForSystem, deleteDraftConfirmOpen, openEditDialog]);

  useEffect(() => {
    const toConfirm = drafts?.filter(draft => Boolean(draft.pull_request_url))
    let interval: NodeJS.Timeout;
    let timeout: NodeJS.Timeout;
    if (toConfirm) {
      // two minutes latency
      timeout = setTimeout(async () => {
        // check every minute
        interval = setInterval(async () => {
          const base = await discoveryApi.getBaseUrl('amqp-topology-manager');
          let hasDraftBeenCompleted = false;
          for (const draft of toConfirm) {
            const url = new URL(`${base}/drafts/complete`);
            url.searchParams.append('componentEntityRef', draft.component_entity_ref);

            const response = await fetchApi.fetch(url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ projectRepo: entity.metadata.annotations?.['dev.azure.com/project-repo'], buildDefinitionName: entity.metadata.annotations?.['dev.azure.com/pipelines'] })
            });

            if (response.ok) {
              // if any ok, then, request refresh catalog, notify completition
              hasDraftBeenCompleted = true;
            }

          }

          if (hasDraftBeenCompleted) {
            await triggerAmqpCatalogRefresh(discoveryApi, fetchApi);
            try {
              await reloadResources().catch(() => { /* ignore */ });
            } catch (error) {
              /* ignore */
            }
          }

        }, 60000);

      }, 5000);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [fetchDraftsForSystem, drafts, discoveryApi, fetchApi, reloadResources, entity]);

  const onServiceSelectionChanged = (selected: ComponentEntity) => {
    setService(selected);
    setOpenEditDialog(true);
  };

  const handleEditDialogClose = () => {
    setOpenEditDialog(false);
  };

  const onConfirmDelete = async () => { 
    setDeleteDraftConfirmOpen(false); 
    await callDraftDelete(); 
  }

  const onDraftDeleted = () => fetchDraftsForSystem();

  return (<>
    <Accordion expanded>
      <AccordionSummary>
        <ListItemText secondary="Only one draft, per service, is allowed.">
          <Typography variant="h5">Drafts</Typography>
        </ListItemText>
      </AccordionSummary>
      <AccordionDetails>
        {drafts &&
          <List dense>
            {services.map((svc, index) => (
              <ServiceDraftItem key={index} service={svc} entity={entity} drafts={drafts}
                onRequestDraftDelete={(callDelete) => { setService(svc); setDeleteDraftConfirmOpen(true); setCallDraftDelete(() => callDelete) }}
                onDraftDeleted={onDraftDeleted}
                onServiceSelectionChanged={() => onServiceSelectionChanged(svc)}
              />
            ))}
          </List>
        }
      </AccordionDetails>
    </Accordion>
    {service && (
      <>
        <TopologyEditDialog
          open={openEditDialog}
          onClose={handleEditDialogClose}
          service={service}
          loadResources={reloadResources}
          entity={entity}
        />
        <SimpleConfirmDialog
          open={deleteDraftConfirmOpen}
          title={`Discard draft for ${service.metadata.name}`}
          text="Are you sure you want to discard the draft?"
          onCancel={() => setDeleteDraftConfirmOpen(false)}
          onConfirm={onConfirmDelete} />
      </>
    )}
  </>);
}

async function triggerAmqpCatalogRefresh(discoveryApi: DiscoveryApi, fetchApi: FetchApi) {
  const urlCatalog = `${await discoveryApi.getBaseUrl(
    'catalog'
  )}/cloud-amqp/development/refresh`;

  await fetchApi.fetch(urlCatalog, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });
}
